import type { School } from 'payloadcms/payload-types'

/** Share of a year level's enrolments at which waiting becomes its norm. */
export const TIPPING_POINT_MIN_SHARE = 0.25
/** Share of a year level's enrolments that makes a cohort "Strength in numbers". */
export const STRENGTH_IN_NUMBERS_MIN_SHARE = 0.2
/** Share of a year level's enrolments at which a cohort's momentum is building. */
export const MOMENTUM_MIN_SHARE = 0.1
export const FINAL_YEAR_GROUP = 12
/** Last year level counted as primary school. */
export const FINAL_PRIMARY_YEAR_LEVEL = 6
export const MAX_TICKETS_PER_REGISTRATION = 3

/** Enums */
export const states = [
  'ACT',
  'NSW',
  'NT',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA',
] as const

export const schoolTypes = [
  {
    label: 'Primary',
    value: 'primary',
  },
  {
    label: 'Secondary',
    value: 'secondary',
  },
  {
    label: 'Combined',
    value: 'combined',
  },
  {
    label: 'Special',
    value: 'special',
  },
] as const

export const schoolSectors = [
  {
    label: 'Government',
    value: 'government',
  },
  {
    label: 'Non-Government',
    value: 'non_government',
  },
] as const

/**
 * How long a child commits to wait, as the pledge form offers it.
 *
 * `yearLevel` is the same numbering `getSchoolYearGroups` puts in each option's
 * `value`, so a pledge can be measured against the year group a child is in
 * today without re-deriving the year from this array's order — which silently
 * shifts the moment an option is added or moved. Null for "I'll decide later",
 * which names no year.
 */
export const finalPledgeYearOptions = [
  {
    label: 'Year 7',
    value: 'year_7',
    yearLevel: 7,
  },
  {
    label: 'Year 8',
    value: 'year_8',
    yearLevel: 8,
  },
  {
    label: 'Year 9',
    value: 'year_9',
    yearLevel: 9,
  },
  {
    label: 'Year 10',
    value: 'year_10',
    yearLevel: 10,
  },
  {
    label: 'Year 11',
    value: 'year_11',
    yearLevel: 11,
  },
  {
    label: 'Year 12',
    value: 'year_12',
    yearLevel: 12,
  },
  {
    label: 'I’ll decide later',
    value: 'undecided',
    yearLevel: null,
  },
] as const

/**
 * The pledge ladder's own label for a final pledge year — "Year 9" — or null
 * for a value that names no year: "I'll decide later", a missing pledge, or
 * anything unrecognised. Callers supply their own wording for that case, since
 * the emails and the admin word it differently.
 *
 * Reads the label off the options above rather than rebuilding it from the
 * stored value, so the list stays the one place a year is named.
 */
export const getFinalPledgeYearLabel = (
  finalPledgeYear?: string | null,
): string | null => {
  const option = finalPledgeYearOptions.find(
    ({ value }) => value === finalPledgeYear,
  )

  return option && option.yearLevel !== null ? option.label : null
}

/** Utilities */

/**
 * Takes a relationship field and returns the corresponding document ID
 * @param idOrDoc A relationship field
 * @returns The document ID
 */
export const getRelationshipId = <T extends { id: string | number }>(
  idOrDoc: string | number | T,
): T['id'] => {
  return typeof idOrDoc === 'string' || typeof idOrDoc === 'number'
    ? (idOrDoc as T['id'])
    : idOrDoc.id
}

/**
 * Returns the year groups for a school, based on the state of the school.
 * @param school The school to get the year groups for
 * @returns An array of year group options
 */
export const getSchoolYearGroups = (
  school?: Pick<School, 'location' | 'type'> | null,
) => {
  if (!school) return []

  const options: { label: string; value: number }[] = []

  if (school.type !== 'secondary') {
    options.push(
      {
        label: 'Year 1',
        value: 1,
      },
      {
        label: 'Year 2',
        value: 2,
      },
      {
        label: 'Year 3',
        value: 3,
      },
      {
        label: 'Year 4',
        value: 4,
      },
      {
        label: 'Year 5',
        value: 5,
      },
      {
        label: 'Year 6',
        value: 6,
      },
    )

    switch (school?.location?.state) {
      case 'NT':
        options.unshift({
          label: 'Transition',
          value: 0,
        })

        options.unshift({
          label: 'Pre-school',
          value: -1,
        })
        break
      case 'QLD':
        options.unshift({
          label: 'Prep',
          value: 0,
        })
        break
      case 'SA':
        options.unshift({
          label: 'Reception',
          value: 0,
        })
        break
      case 'VIC':
        options.unshift({
          label: 'Prep',
          value: 0,
        })
        break
      case 'TAS':
        options.unshift({
          label: 'Kindergarten',
          value: 0,
        })

        options.unshift({
          label: 'Prep',
          value: -1,
        })
        break
      case 'WA':
        options.unshift({
          label: 'Pre-Primary',
          value: 0,
        })

        options.unshift({
          label: 'Kindergarten',
          value: -1,
        })
        break
      default:
        options.unshift({
          label: 'Kindergarten',
          value: 0,
        })
        break
    }
  }

  if (school.type !== 'primary') {
    options.push(
      {
        label: 'Year 7',
        value: 7,
      },
      {
        label: 'Year 8',
        value: 8,
      },
      {
        label: 'Year 9',
        value: 9,
      },
      {
        label: 'Year 10',
        value: 10,
      },
      {
        label: 'Year 11',
        value: 11,
      },
      {
        label: 'Year 12',
        value: 12,
      },
    )
  }

  return options
}

/**
 * A school as the enrolment helpers read it: its own figures, plus the parent
 * school they fall back to when it has none.
 */
export type SchoolEnrolmentSource = Pick<School, 'enrolments' | 'parentSchool'>

/**
 * A school's ACARA enrolment figures, falling back to its parent school's when
 * it has none of its own.
 *
 * ACARA reports enrolments against the parent school rather than each campus, so
 * a campus can carry no figures at all while the school it belongs to holds the
 * full breakdown. Without the fallback those campuses have no denominator: every
 * share comes back null and the school reads as empty however many families have
 * committed.
 *
 * `total` is the whole emptiness test, and the parent's group is taken as a unit
 * rather than merged field by field. One hop is always enough — the ACARA import
 * refuses a parent that is itself a campus.
 *
 * The parent has to be populated to be used: read at `depth: 0` it is a bare id,
 * and the school keeps its own figures.
 * @param school The school to read enrolments from
 * @returns The figures to use, and the school they came from when inherited
 */
export const resolveEnrolments = (
  school?: SchoolEnrolmentSource | null,
): { enrolments: School['enrolments']; inheritedFrom: School | null } => {
  if (school?.enrolments?.total)
    return { enrolments: school.enrolments, inheritedFrom: null }

  const parent = school?.parentSchool

  if (typeof parent === 'object' && parent !== null && parent.enrolments?.total)
    return { enrolments: parent.enrolments, inheritedFrom: parent }

  return { enrolments: school?.enrolments, inheritedFrom: null }
}

/**
 * Returns a school's ACARA enrolment counts keyed by year level, ready to index
 * with the `value` of a `getSchoolYearGroups` option. A missing key means the
 * year group is not offered; a `null` value means ACARA suppressed the count
 * because fewer than five students are enrolled. Falls back to the parent
 * school's figures when the school has none of its own.
 * @param school The school to read enrolments from
 * @returns Enrolments keyed by year level, or an empty object if none are stored
 */
export const getEnrolmentsByYearLevel = (
  school?: SchoolEnrolmentSource | null,
): Record<number, number | null> =>
  (resolveEnrolments(school).enrolments?.byYearLevel as Record<
    number,
    number | null
  >) ?? {}

/**
 * One cohort's share of its own year level — committed students over that year
 * level's ACARA enrolment. The single definition of the figure the school page's
 * year group rows, the Strength in Numbers flag and the commitment email all
 * read.
 * @param school The school to read enrolments from
 * @param cohort The cohort to measure
 * @param currentYear The year to resolve the year level against (defaults to now)
 * @returns Share in [0, ∞), or null when the year level is not offered or ACARA
 *   suppressed its count — there is nothing to take a share of either way
 */
export const getYearLevelShare = (
  school: SchoolEnrolmentSource | null | undefined,
  cohort:
    | { graduationYear?: number | null; uniqueStudentCount?: number | null }
    | null
    | undefined,
  currentYear: number = new Date().getFullYear(),
): number | null => {
  if (!cohort?.graduationYear) return null

  const enrolled =
    getEnrolmentsByYearLevel(school)[
      currentYear + FINAL_YEAR_GROUP - cohort.graduationYear
    ]

  if (!enrolled) return null

  return (cohort.uniqueStudentCount ?? 0) / enrolled
}

/**
 * A share rendered as a whole-percent for display. Floors rather than rounds so
 * the figure shown never outruns the milestone gate, which clears on the raw
 * share with `>=` — a year group reads "N% committed" exactly when it has truly
 * reached N%, so the number and its badge flip together.
 * @param share A share in [0, ∞), as `getYearLevelShare` returns it
 * @returns The share as a whole percent, floored
 */
export const sharePercent = (share: number): number => Math.floor(share * 100)

/** Where a cohort sits on the movement scale, as told to a parent who has just
 *  committed. Unlike the school page's tiers, which measure a cohort against its
 *  year level's enrolments, this reads purely off arrival order: the movement is
 *  young until enough parents have shown up, however big the year level is. */
export type CommitmentScenario = 'start' | 'started' | 'momentum'

/** Arrival position at which a cohort stops being founded and starts building. */
export const MOMENTUM_MIN_POSITION = 6

/**
 * @param position Where the committing parent lands among the cohort's parents,
 *   counting themselves — 1 for the founder
 * @returns The scenario whose copy the commitment email should use
 */
export const getCommitmentScenario = (position: number): CommitmentScenario => {
  if (position <= 1) return 'start'
  if (position >= MOMENTUM_MIN_POSITION) return 'momentum'

  return 'started'
}

/**
 * A school's primary commitment progress — the stat its school page leads with:
 * committed students over ACARA enrolments, summed across the primary year
 * levels. Levels with a missing or suppressed enrolment sit out of both sides
 * of the ratio. Null for schools without primary year groups and when nothing
 * is measurable, so every surface showing this hides the figure on null rather
 * than showing 0.
 * @param school The school (type, location and enrolments are read)
 * @param cohorts The school's current cohorts (graduated ones are simply never matched)
 * @param currentYear The year to resolve year levels against (defaults to now)
 * @returns Share in [0, ∞) — callers clamp for display — or null when not applicable
 */
export const getPrimarySchoolProgress = (
  school:
    | (Pick<School, 'type' | 'location'> & SchoolEnrolmentSource)
    | null
    | undefined,
  cohorts: readonly {
    graduationYear?: number | null
    uniqueStudentCount?: number | null
  }[],
  currentYear: number = new Date().getFullYear(),
): number | null => {
  if (school?.type !== 'primary' && school?.type !== 'combined') return null

  const enrolments = getEnrolmentsByYearLevel(school)
  let enrolled = 0
  let members = 0

  for (const { value } of getSchoolYearGroups(school)) {
    if (value > FINAL_PRIMARY_YEAR_LEVEL) continue
    const yearEnrolled = enrolments[value]
    if (yearEnrolled == null) continue

    enrolled += yearEnrolled
    const graduationYear = getGraduationYearForLevel(value, currentYear)
    members +=
      cohorts.find((cohort) => cohort.graduationYear === graduationYear)
        ?.uniqueStudentCount ?? 0
  }

  return enrolled ? members / enrolled : null
}

/**
 * Returns the current year group for a cohort, based on the graduation year and School state.
 * @param graduationYear The graduation year of the cohort
 * @param school The school to get the current year group for
 * @returns The current year group
 */
export const getCurrentYearGroup = (
  graduationYear: number,
  school: Pick<School, 'location' | 'type'>,
) => {
  const currentYear = new Date().getFullYear()
  const yearGroups = getSchoolYearGroups(school)

  const yearGroupValue = graduationYear
    ? currentYear + FINAL_YEAR_GROUP - graduationYear
    : null

  return (
    yearGroups.find((yearGroup) => yearGroup.value === yearGroupValue)?.label ??
    `Graduated ${school.type === 'primary' ? graduationYear - 6 : graduationYear}`
  )
}

/**
 * Resolves a current year level to its graduation year (the inverse of
 * getCurrentYearGroup). State/school-type independent: the numeric year level
 * maps to a graduation year identically everywhere; only the label of the
 * youngest levels varies by state.
 * @param yearLevel The current year level (0 = Kindergarten/Prep, 1 = Year 1, … 12 = Year 12)
 * @param currentYear The year to resolve against (defaults to now)
 * @returns The Year 12 graduation year for that level
 */
export const getGraduationYearForLevel = (
  yearLevel: number,
  currentYear: number = new Date().getFullYear(),
) => currentYear + FINAL_YEAR_GROUP - yearLevel

/**
 * Checks if a cohort has graduated
 * @param graduationYear The graduation year of the cohort
 * @param school The school to check if the cohort has graduated
 * @returns True if the cohort has graduated, false otherwise
 */
export const hasGraduated = (
  graduationYear: number,
  school: Pick<School, 'location' | 'type'>,
) => {
  const currentYear = new Date().getFullYear()
  const yearGroups = getSchoolYearGroups(school)

  const yearGroupValue = graduationYear
    ? currentYear + FINAL_YEAR_GROUP - graduationYear
    : null

  return !yearGroups.find((yearGroup) => yearGroup.value === yearGroupValue)
}

/**
 * Pluralises a word based on the count
 * @param count The count of the word
 * @param singular The word to pluralise
 * @param plural The plural form of the word
 * @returns The pluralised word
 */
export const pluralise = (
  count: number,
  singular: string,
  plural = `${singular}s`,
) => {
  return [count, count === 1 ? singular : plural].join(' ')
}

/**
 * School URLs
 *
 * Slugs are unique per school, but only because a backfill made them so — before that,
 * schools sharing a name shared a slug and every one of these links could land on the wrong
 * school. Building them in one place keeps the shapes honest: the main site serves school
 * pages from the root, and the South Australia pages live under `/sa` on that same site.
 */
interface SlugOnlySchool {
  slug?: string | null
  name?: string | null
}

const requireSlug = ({ slug, name }: SlugOnlySchool) => {
  // Callers routinely fetch schools with a narrow `select`, and a missing slug would quietly
  // produce a link to the site root. In an email that reads as a broken CTA to the reader
  // and as nothing at all to us, so fail loudly instead.
  if (!slug) {
    throw new Error(
      `Cannot build a URL for school ${name ?? '(unnamed)'}: it has no slug.`,
    )
  }

  return slug
}

const withoutTrailingSlash = (baseUrl: string) => baseUrl.replace(/\/+$/, '')

export const getSchoolPath = (school: SlugOnlySchool) =>
  `/${requireSlug(school)}`

export const getSchoolUrl = (school: SlugOnlySchool, baseUrl: string) =>
  `${withoutTrailingSlash(baseUrl)}${getSchoolPath(school)}`

/**
 * South Australia paths
 *
 * The SA microsite used to be its own app on its own domain. It now lives under `/sa` on the
 * main site, so every SA link — pages, emails and the Payload page `uri` — is built from these
 * rather than from a separate site URL.
 */
export const SA_BASE_PATH = '/sa'

export const getSaPath = (uri = '') => `${SA_BASE_PATH}${uri}`

export const getSaSchoolPath = (school: SlugOnlySchool) =>
  getSaPath(`/schools/${requireSlug(school)}`)

export const getSaSchoolUrl = (school: SlugOnlySchool, baseUrl: string) =>
  `${withoutTrailingSlash(baseUrl)}${getSaSchoolPath(school)}`

interface ShortIdOnlyEvent {
  shortId?: string | null
  title?: string | null
}

const requireShortId = ({ shortId, title }: ShortIdOnlyEvent) => {
  // Same reasoning as requireSlug: an event without a shortId would silently link to the SA
  // landing page instead of the event, which reads as a broken CTA in an email.
  if (!shortId) {
    throw new Error(
      `Cannot build a URL for event ${title ?? '(untitled)'}: it has no shortId.`,
    )
  }

  return shortId
}

export const getSaEventRequestPath = () => getSaPath('/events/request')

export const getSaEventPath = (event: ShortIdOnlyEvent) =>
  getSaPath(`/events/${requireShortId(event)}`)

export const getSaEventUrl = (event: ShortIdOnlyEvent, baseUrl: string) =>
  `${withoutTrailingSlash(baseUrl)}${getSaEventPath(event)}`

interface RegistrationIdentity {
  id?: number | string | null
  email?: string | null
}

/**
 * The cancel page identifies the registration from the query string, so both parts have to
 * survive being pasted out of an email client — hence encoding the email rather than
 * interpolating it raw.
 */
export const getSaEventCancelPath = (
  event: ShortIdOnlyEvent,
  { id, email }: RegistrationIdentity,
) => {
  const query = new URLSearchParams()

  if (id !== null && id !== undefined) query.set('id', String(id))
  if (email) query.set('email', email)

  const search = query.toString()

  return `${getSaEventPath(event)}/cancel${search ? `?${search}` : ''}`
}

export const getSaEventCancelUrl = (
  event: ShortIdOnlyEvent,
  registration: RegistrationIdentity,
  baseUrl: string,
) =>
  `${withoutTrailingSlash(baseUrl)}${getSaEventCancelPath(event, registration)}`

/**
 * A first name has to carry at least this many letters. Parents were entering a
 * bare initial, which is indistinguishable from a real one-letter name, so the
 * count is what separates "Jo" from "J".
 */
export const MIN_FIRST_NAME_LETTERS = 2
export const FIRST_NAME_REQUIRED_MESSAGE = 'First name is required.'
export const FIRST_NAME_TOO_SHORT_MESSAGE = 'Please enter your full first name.'
export const CHILD_FIRST_NAME_TOO_SHORT_MESSAGE =
  'Please enter your child’s full first name.'

/**
 * Counts letters rather than characters so padding and punctuation can't pass a
 * length check ("J.", " A "), and matches any script's letters so accented names
 * ("Sé") aren't turned away.
 */
export const isValidFirstName = (value: string) =>
  (value.match(/\p{L}/gu) ?? []).length >= MIN_FIRST_NAME_LETTERS

export const MAX_FIRST_NAME_LENGTH = 50
export const FIRST_NAME_INVALID_MESSAGE =
  'Please enter a name without links or symbols.'

/**
 * First names are printed into emails we send, so one that reads as a link
 * ("Claim your prize at evil.com") would be delivered under our name. A dot
 * only counts when a word follows it directly, which keeps "J.R." and
 * "St. John".
 */
export const isSafeFirstName = (value: string) =>
  value.trim().length <= MAX_FIRST_NAME_LENGTH &&
  !/[/:@<>\\]|www\.|\.\p{L}{2,}/iu.test(value)
